<template>
  <!-- Dashboard Builder v1 creation chooser (spec §6). Flag-on replacement for the
       direct name dialog on "Add Dashboard". Industrial-flat, no accent bars. -->
  <v-dialog :value="value" max-width="560" persistent @input="onInput">
    <v-card class="ds-dialog cdb-chooser">
      <div class="ds-dialog__header">
        <div class="ds-dialog__header-icon">
          <v-icon color="primary" size="22">mdi-view-dashboard-outline</v-icon>
        </div>
        <div class="ds-dialog__header-text">
          <span class="ds-dialog__title">Add Dashboard</span>
          <span class="ds-dialog__subtitle">Choose a starting point</span>
        </div>
        <v-btn icon small aria-label="Close" :disabled="creating" @click="close">
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="ds-dialog__body cdb-chooser__body">
        <button
          type="button"
          class="cdb-choice"
          :disabled="creating"
          @click="select('blank')">
          <v-icon class="cdb-choice__icon" color="primary">mdi-file-outline</v-icon>
          <span class="cdb-choice__text">
            <span class="cdb-choice__title">Blank dashboard</span>
            <span class="cdb-choice__desc">Start empty and add widgets yourself.</span>
          </span>
          <v-icon class="cdb-choice__chevron">mdi-chevron-right</v-icon>
        </button>

        <button
          type="button"
          class="cdb-choice"
          :class="{ 'cdb-choice--disabled': !duplicateState.available }"
          :disabled="creating || !duplicateState.available"
          @click="select('duplicate')">
          <v-icon class="cdb-choice__icon" color="primary">mdi-content-copy</v-icon>
          <span class="cdb-choice__text">
            <span class="cdb-choice__title">Duplicate existing dashboard…</span>
            <span class="cdb-choice__desc">{{ duplicateDescription }}</span>
          </span>
          <v-icon class="cdb-choice__chevron">{{ duplicateState.available ? 'mdi-chevron-right' : 'mdi-lock-outline' }}</v-icon>
        </button>

        <button
          type="button"
          class="cdb-choice"
          :class="{ 'cdb-choice--disabled': !starterState.available }"
          :disabled="creating || !starterState.available"
          @click="select('starter')">
          <v-icon class="cdb-choice__icon" color="primary">mdi-star-outline</v-icon>
          <span class="cdb-choice__text">
            <span class="cdb-choice__title">Starter: Facility overview</span>
            <span class="cdb-choice__desc">{{ starterDescription }}</span>
          </span>
          <v-icon v-if="starterState.loading" class="cdb-choice__chevron cdb-choice__chevron--spin">mdi-loading</v-icon>
          <v-icon v-else class="cdb-choice__chevron">{{ starterState.available ? 'mdi-chevron-right' : 'mdi-lock-outline' }}</v-icon>
        </button>
      </div>

      <div class="ds-dialog__footer">
        <span class="ds-dialog__hint">Blank creates an empty tab you can fill with widgets.</span>
        <v-spacer></v-spacer>
        <v-btn outlined color="error" :disabled="creating" @click="close">Cancel</v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'DashboardCreationChooser',
  props: {
    /** Dialog open state (v-model). */
    value: { type: Boolean, default: false },
    /** { available, reason, loading } — Starter is enabled only when the transactional
     *  endpoint exists AND preflight resolves; the reason is shown when disabled. */
    starterState: {
      type: Object,
      default: () => ({ available: false, reason: '', loading: false }),
    },
    /** { available, reason } — Duplicate is enabled only when the endpoint exists AND
     *  there is a dashboard to copy. */
    duplicateState: {
      type: Object,
      default: () => ({ available: false, reason: '' }),
    },
    /** True while a transactional creation is in flight (double-submit guard, spec §6). */
    creating: { type: Boolean, default: false },
  },
  computed: {
    duplicateDescription() {
      return this.duplicateState.available
        ? 'Copy a dashboard and its widgets, then rename it.'
        : this.duplicateState.reason;
    },
    starterDescription() {
      if (this.starterState.loading) return 'Checking this facility’s data sources…';
      return this.starterState.available
        ? 'A ready-made integrity overview of anomalies, CUI and equipment.'
        : this.starterState.reason;
    },
  },
  methods: {
    onInput(v) {
      this.$emit('input', v);
    },
    close() {
      if (this.creating) return;
      this.$emit('input', false);
    },
    select(kind) {
      this.$emit('select', kind);
    },
  },
};
</script>

<style scoped lang="scss">
.cdb-chooser__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cdb-choice {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px 16px;
  text-align: left;
  border: 1px solid rgba(79, 79, 79, 0.28);
  border-radius: 8px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.cdb-choice:hover:not(:disabled) {
  border-color: var(--v-primary-base);
  background: rgba(3, 98, 76, 0.05);
}

.cdb-choice:focus-visible {
  outline: 2px solid var(--v-primary-base);
  outline-offset: 2px;
}

.cdb-choice:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.cdb-choice__icon {
  flex-shrink: 0;
}

.cdb-choice__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.cdb-choice__title {
  font-weight: 600;
  font-size: 0.95rem;
}

.cdb-choice__desc {
  font-size: 0.8125rem;
  opacity: 0.62;
  margin-top: 2px;
}

.cdb-choice__chevron {
  flex-shrink: 0;
  opacity: 0.5;
}

.cdb-choice__chevron--spin {
  animation: cdb-chooser-spin 0.9s linear infinite;
}

@keyframes cdb-chooser-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.theme--dark .cdb-choice {
  border-color: rgba(255, 255, 255, 0.18);
}

.theme--dark .cdb-choice:hover:not(:disabled) {
  background: rgba(44, 194, 149, 0.08);
}
</style>
